import * as React from 'react';

interface PurchaseConfirmationEmailProps {
  name?: string;
  company?: string;
  orderNumber?: string;
  orderDate?: string;
  items?: Array<{
    name: string;
    description?: string;
    period?: string;
    nextBilling?: string;
    quantity: number;
    price: number;
  }>;
  total?: number;
  paymentMethod?: string;
  deliveryMethod?: string;
  supportEmail?: string;
}

export const PurchaseConfirmationEmail: React.FC<PurchaseConfirmationEmailProps> = ({
  name = 'João Silva',
  company = 'SaaS Trivium',
  orderNumber = 'ORD-12345',
  orderDate = '10/06/2023',
  items = [
    { 
      name: 'Plano Profissional', 
      description: 'Acesso completo a todas as funcionalidades premium',
      period: 'Mensal',
      nextBilling: '14 Abr, 2025',
      quantity: 1, 
      price: 399.00 
    },
    { 
      name: 'Addon Integrações', 
      description: 'Conecte com qualquer plataforma externa',
      period: 'Mensal',
      nextBilling: '14 Abr, 2025',
      quantity: 1, 
      price: 99.00 
    },
    { 
      name: 'Addon Analytics Avançado', 
      description: 'Relatórios detalhados e insights de negócio',
      period: 'Mensal',
      nextBilling: '14 Abr, 2025',
      quantity: 2, 
      price: 79.50 
    },
    { 
      name: 'Suporte Prioritário', 
      description: 'Atendimento em até 2 horas por especialistas',
      period: 'Anual',
      nextBilling: '10 Mar, 2026',
      quantity: 1, 
      price: 199.00 
    },
  ],
  total = 856.00,
  paymentMethod = 'Cartão de Crédito',
  deliveryMethod = 'Acesso Digital',
  supportEmail = 'suporte@saasTrivium.com',
}) => {
  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <img 
          src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Logo%20Trivium%20Rebranded%20Branco%20150%20dpi-0VfiiO2tOZNdZlvY5A7z3saYZGysgq.png" 
          alt="Trivium"
          style={logoImageStyle}
        />
      </div>
      
      {/* Content */}
      <div style={contentStyle}>
        <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={{ backgroundColor: '#E7F2FF' }}>
          <tr>
            <td align="center" style={{ padding: '40px 20px' }}>
              <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={{ maxWidth: '400px' }}>
                {/* Círculo com check */}
                <tr>
                  <td align="center" style={{ paddingBottom: '30px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: '#1F1746', 
                      display: 'inline-block',
                      textAlign: 'center',
                      lineHeight: '60px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
                      </svg>
                    </div>
                  </td>
                </tr>
                
                {/* Título */}
                <tr>
                  <td align="center" style={{ paddingBottom: '15px' }}>
                    <h1 style={{ 
                      margin: '0',
                      fontSize: '28px',
                      color: '#333333',
                      fontWeight: '600'
                    }}>
                      Obrigado pela sua assinatura!
                    </h1>
                  </td>
                </tr>
                
                {/* Mensagem */}
                <tr>
                  <td align="center" style={{ paddingBottom: '25px' }}>
                    <p style={{ 
                      margin: '0',
                      fontSize: '16px',
                      color: '#666666'
                    }}>
                      Seu pedido foi processado com sucesso.
                    </p>
                  </td>
                </tr>
                
                {/* Badge do pedido */}
                <tr>
                  <td align="center">
                    <div style={{ 
                      backgroundColor: '#FFD600',
                      borderRadius: '24px',
                      color: '#333333',
                      fontWeight: '600',
                      fontSize: '15px',
                      padding: '12px 25px',
                      display: 'inline-block'
                    }}>
                      Pedido #ORD-12345
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        
        <div style={contentContainerStyle}>
          <h2 style={titleStyle}>Olá, {name}!</h2>
        </div>
        
        <div style={orderSummaryStyle}>
          <div style={orderHeaderStyle}>
            <h3 style={orderTitleStyle}>Detalhes do Pedido</h3>
          </div>
          
          <div style={orderDetailsStyle}>
            <p style={detailRowStyle}>
              <span style={detailLabelStyle}>Data:</span>
              <span style={detailValueStyle}>{orderDate}</span>
            </p>
            <p style={detailRowStyle}>
              <span style={detailLabelStyle}>Método de Pagamento:</span>
              <span style={detailValueStyle}>{paymentMethod}</span>
            </p>
          </div>
          
          <table style={itemsTableStyle}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Item</th>
                <th style={tableHeaderCenterStyle}>Qtd</th>
                <th style={tableHeaderRightStyle}>Preço</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <React.Fragment key={index}>
                  <tr style={tableRowStyle}>
                    <td style={tableCellStyle}>
                      <div style={itemNameStyle}>{item.name}</div>
                      {item.description && (
                        <div style={itemDescriptionStyle}>{item.description}</div>
                      )}
                      {item.period && (
                        <div style={itemDetailStyle}>Período: {item.period}</div>
                      )}
                      {item.nextBilling && (
                        <div style={itemDetailStyle}>Próxima cobrança: {item.nextBilling}</div>
                      )}
                    </td>
                    <td style={tableCellCenterStyle}>{item.quantity}</td>
                    <td style={tableCellRightStyle}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.price)}
                    </td>
                  </tr>
                  {index < items.length - 1 && (
                    <tr>
                      <td colSpan={3} style={itemSeparatorStyle}></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr style={totalRowStyle}>
                <td colSpan={2} style={totalLabelStyle}>Total</td>
                <td style={totalValueStyle}>
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style={nextStepsStyle}>
          <h3 style={nextStepsTitleStyle}>Próximos Passos</h3>
          <p style={paragraphStyle}>
            Você receberá acesso imediato à sua assinatura. Nossa equipe está à disposição caso precise de qualquer auxílio.
          </p>
          <div style={buttonContainerStyle}>
            <a href="#" style={buttonStyle}>
              Acessar Minha Conta
            </a>
          </div>
        </div>
        
        <hr style={hrStyle} />
        
        <div style={footerContentStyle}>
          <p style={footerStyle}>
            Se você tiver alguma dúvida, entre em contato conosco:
          </p>
          <div style={contactChannelsStyle}>
            <p style={contactChannelStyle}>✉️ suporte@trivium.com.br&nbsp;&nbsp;📱 (11) 3181-5940</p>
          </div>
          <p style={footerStyle}>
            Atenciosamente,<br />
            Equipe {company}
          </p>
        </div>
      </div>
      
      {/* Footer */}
      <div style={emailFooterStyle}>
        <p style={copyrightStyle}>
          © 2025 Trivium. Todos os direitos reservados.
        </p>
        <div style={footerLinksStyle}>
          <a href="#" style={footerLinkStyle}>Termos de Uso</a>
          <span style={footerDividerStyle}>|</span>
          <a href="#" style={footerLinkStyle}>Política de Privacidade</a>
        </div>
      </div>
    </div>
  );
};

// Estilos
const brandColor = '#1F1746';
const accentColor = '#3E5FBA';
const backgroundColor = '#F5F7FA';
const textColor = '#333333';
const secondaryTextColor = '#666666';

const containerStyle = {
  fontFamily: "'Satoshi', Arial, sans-serif",
  maxWidth: '600px',
  margin: '0 auto',
  backgroundColor: backgroundColor,
};

const headerStyle = {
  backgroundColor: brandColor,
  padding: '25px',
  textAlign: 'center' as const,
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
};

const logoImageStyle = {
  maxWidth: '180px',
  height: 'auto',
  display: 'block',
  margin: '0 auto',
};

const contentStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '0 0 8px 8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
  padding: '0',
  margin: '0 0 20px 0',
  overflow: 'hidden',
};

const successHeaderStyle = {
  backgroundColor: '#E7F2FF',
  padding: '20px 0',
};

const checkmarkCircleStyle = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: '#1F1746',
};

const thankYouContainerStyle = {
  maxWidth: '300px',
  margin: '0 auto',
};

const thankYouTitleStyle = {
  margin: '0 0 10px 0',
  fontSize: '28px',
  color: '#333333',
  fontWeight: '600',
  lineHeight: '1.2',
  textAlign: 'center' as const,
};

const successMessageStyle = {
  margin: '0',
  fontSize: '16px',
  color: '#666666',
  fontWeight: '400',
  lineHeight: '1.4',
  textAlign: 'center' as const,
};

const orderBadgeStyle = {
  backgroundColor: '#FFD600',
  borderRadius: '15px',
  color: '#333333',
  fontWeight: '600',
  fontSize: '16px',
  padding: '15px 20px',
  display: 'inline-block',
  textAlign: 'center' as const,
  lineHeight: '1.4',
  minWidth: '120px',
};

const titleStyle = {
  fontSize: '24px',
  fontWeight: '600',
  color: brandColor,
  marginBottom: '16px',
  padding: '24px 40px 0 40px',
};

const paragraphStyle = {
  fontSize: '16px',
  lineHeight: '24px',
  color: textColor,
  margin: '16px 0',
  padding: '0 40px',
};

const orderSummaryStyle = {
  border: '1px solid #E1E4E8',
  borderRadius: '8px',
  overflow: 'hidden',
  margin: '24px 40px',
};

const orderHeaderStyle = {
  backgroundColor: brandColor,
  padding: '16px 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const orderTitleStyle = {
  margin: '0',
  color: '#ffffff',
  fontSize: '18px',
  fontWeight: '600',
};

const orderDetailsStyle = {
  padding: '20px 24px',
  backgroundColor: '#F8FAFC',
  borderBottom: '1px solid #E1E4E8',
};

const detailRowStyle = {
  margin: '8px 0',
  fontSize: '14px',
  display: 'flex',
  justifyContent: 'space-between',
};

const detailLabelStyle = {
  color: secondaryTextColor,
  fontWeight: '500',
};

const detailValueStyle = {
  color: textColor,
  fontWeight: '600',
};

const itemsTableStyle = {
  width: '100%',
  borderCollapse: 'collapse' as const,
};

const tableHeaderStyle = {
  textAlign: 'left' as const,
  padding: '12px 24px',
  borderBottom: '1px solid #E1E4E8',
  fontSize: '14px',
  fontWeight: '600',
  color: secondaryTextColor,
};

const tableHeaderCenterStyle = {
  ...tableHeaderStyle,
  textAlign: 'center' as const,
};

const tableHeaderRightStyle = {
  ...tableHeaderStyle,
  textAlign: 'right' as const,
};

const tableRowStyle = {
  borderBottom: '1px solid #E1E4E8',
};

const tableCellStyle = {
  padding: '20px 24px',
  fontSize: '14px',
  color: textColor,
  verticalAlign: 'top' as const,
};

const tableCellCenterStyle = {
  ...tableCellStyle,
  textAlign: 'center' as const,
};

const tableCellRightStyle = {
  ...tableCellStyle,
  textAlign: 'right' as const,
  fontWeight: '600',
};

const totalRowStyle = {
  backgroundColor: '#F8FAFC',
};

const totalLabelStyle = {
  padding: '16px 24px',
  fontSize: '16px',
  fontWeight: '600',
  color: brandColor,
  textAlign: 'right' as const,
};

const totalValueStyle = {
  padding: '16px 24px',
  fontSize: '16px',
  fontWeight: '700',
  color: brandColor,
  textAlign: 'right' as const,
};

const nextStepsStyle = {
  padding: '8px 0 24px 0',
};

const nextStepsTitleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: brandColor,
  margin: '0 0 16px 0',
  padding: '0 40px',
};

const buttonContainerStyle = {
  textAlign: 'center' as const,
  margin: '32px 0',
};

const buttonStyle = {
  backgroundColor: brandColor,
  borderRadius: '6px',
  color: '#fff',
  fontSize: '16px',
  fontWeight: '600',
  textDecoration: 'none',
  padding: '12px 24px',
  display: 'inline-block',
  transition: 'background-color 0.3s ease',
};

const hrStyle = {
  borderColor: '#E1E4E8',
  margin: '24px 40px',
};

const footerContentStyle = {
  padding: '0 40px 32px 40px',
};

const footerStyle = {
  fontSize: '14px',
  color: secondaryTextColor,
  margin: '8px 0',
};

const emailFooterStyle = {
  backgroundColor: backgroundColor,
  padding: '24px 40px',
  textAlign: 'center' as const,
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
};

const copyrightStyle = {
  fontSize: '13px',
  color: secondaryTextColor,
  margin: '0 0 12px 0',
};

const footerLinksStyle = {
  margin: '8px 0',
};

const footerLinkStyle = {
  color: brandColor,
  textDecoration: 'none',
  fontSize: '13px',
  fontWeight: '500',
};

const footerDividerStyle = {
  color: secondaryTextColor,
  margin: '0 8px',
};

const itemNameStyle = {
  fontWeight: '600',
  fontSize: '15px',
  color: brandColor,
  marginBottom: '4px',
};

const itemDescriptionStyle = {
  fontSize: '13px',
  color: textColor,
  marginBottom: '8px',
  lineHeight: '1.4',
};

const itemDetailStyle = {
  fontSize: '12px',
  color: secondaryTextColor,
  margin: '2px 0',
};

const itemSeparatorStyle = {
  padding: '0',
  height: '10px',
};

const contactChannelsStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  marginBottom: '20px',
  marginTop: '8px',
};

const contactChannelStyle = {
  fontSize: '15px',
  color: textColor,
  margin: '6px 0',
  fontWeight: '500',
};

const contentContainerStyle = {
  padding: '0 40px',
};

export default PurchaseConfirmationEmail; 